import { CartLineItemComponent } from '@msdyn365-commerce/components';
import MsDyn365, * as Msdyn365 from '@msdyn365-commerce/core';
import { isChannelTypeB2B } from '@msdyn365-commerce/core';
import {
    CartLine,
    ChannelDeliveryOptionConfiguration,
    DeliveryModeTypeFilter,
    FeatureState,
    OrgUnitLocation,
    ProductCatalog,
    ProductDeliveryOptions,
    SearchArea,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getProductUrlSync, ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import groupBy from 'lodash/groupBy';
import React from 'react';
import { IEmailDelivery, ILineItem, ILineItemDeliveryGroup, ILineItems, IPickUpAtStore } from '@msdyn365-commerce-modules/checkout';
import { getOrgUnitLocationsByAreaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';
import { getCartState } from '@msdyn365-commerce/global-state';
type deliveryType = 'pickup' | 'ship' | 'email';

export const getLineItems = async (
    context: Msdyn365.ICoreContext,
    products: SimpleProduct[],
    catalogs: ProductCatalog[],
    typeName: string,
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,
    telemetryContent?: ITelemetryContent,
    featureState?: FeatureState[],
    deliveryOptions?: ProductDeliveryOptions[],
    imageSettings?: Msdyn365.IImageSettings
): Promise<ILineItems | undefined> => {
    const pickupDeliveryModeCode = context.request.channel?.PickupDeliveryModeCode;
    const emailDeliveryModeCode = context.request.channel?.EmailDeliveryModeCode;
    const cartState = await getCartState(context.actionContext);
    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    const retailMultiplePickUpOptionEnabled = featureState?.find(item => item.Name === multiplePickupStoreSwitchName)?.IsEnabled;
    const quantityDisplayString = 'Quantity';
    const productDimensionTypeColor = 'Color';
    const productDimensionTypeSize = 'Size';
    const productDimensionTypeStyle = 'Style';
    const productDimensionTypeAmount = 'Amount';
    const configString = 'Configuration';
    const inputQuantityAriaLabel = 'Quantity To Purchase';
    const discountStringText = 'Savings';
    const shippingCharges = 'Shipping charges';
    const multiplePickUpLabel = 'Pickup';
    const shippingLable = 'Shipping';
    const itemLabel = 'item';
    const itemsLabel = 'items';
    const inStorePickUpLabel = 'In-store pickup ({count} {suffix})';
    const shippingCountCheckoutLineItem = 'Shipping ({count} {suffix})';
    const editCartText = 'Edit Cart';
    const searchArea: SearchArea = {
        Latitude: 0,
        Longitude: 0,
        Radius: 0,
        DistanceUnitValue: 0
    };
    const orgUnitLocations = await getOrgUnitLocationsByAreaAsync(
        { callerContext: context.actionContext },
        searchArea,
        DeliveryModeTypeFilter.None
    );
    const _getDeliveryLocation = (cartLine: CartLine): string => {
        if (!cartLine.FulfillmentStoreId || !orgUnitLocations) {
            return '';
        }

        const locationMatch = (orgUnitLocations || []).find((location: OrgUnitLocation): boolean => {
            return location.OrgUnitNumber === cartLine.FulfillmentStoreId;
        });

        return (locationMatch && locationMatch.OrgUnitName) || '';
    };

    const _getProduct = (productId: number): SimpleProduct | undefined => {
        return products.find((_product: SimpleProduct) => {
            return productId === _product.RecordId;
        });
    };

    const _getLineItemComponent = (cartline: CartLine, index: number): JSX.Element | undefined => {
        const product = cartline.ProductId && _getProduct(cartline.ProductId);

        // const catalogs = input.data.catalogs?.result;
        if (!product) {
            return;
        }

        let productUrl = getProductUrlSync(product, context.actionContext, undefined);
        if (MsDyn365.isBrowser && isChannelTypeB2B(context.actionContext.requestContext)) {
            const fullUrl = new URL(productUrl, window.location.href);
            fullUrl.searchParams.set('catalogid', `${cartline.CatalogId ?? 0}`);
            productUrl = fullUrl.href;
        }
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                lg: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                xl: { q: 'w=275&h=275&m=6', w: 275, h: 275 }
            },
            lazyload: true
        };

        return (
            <CartLineItemComponent
                data={{
                    cartLine: cartline,
                    cartState: cartState,
                    product,
                    catalogs: catalogs
                }}
                currentQuantity={cartline.Quantity}
                maxQuantity={context.actionContext.requestContext.app.configmaxQuantityForProductListLineItem}
                isOutOfStock={false}
                gridSettings={context.request.gridSettings || {}}
                imageSettings={imageSettings || defaultImageSettings}
                id={cartline.LineId || ''}
                typeName={typeName}
                productUrl={productUrl}
                context={context}
                key={cartline.LineId}
                lineIndex={index}
                isQuantityEditable={!ObjectExtensions.isNullOrUndefined(product)}
                primaryImageUrl={product?.PrimaryImageUrl}
                isCartStateReady={cartState?.status === 'READY'}
                showShippingChargesForLineItems={false}
                telemetryContent={telemetryContent}
                channelDeliveryOptionConfig={channelDeliveryOptionConfig}
                resources={{
                    sizeString: productDimensionTypeSize,
                    colorString: productDimensionTypeColor,
                    styleString: productDimensionTypeStyle,
                    amountString: productDimensionTypeAmount,
                    quantityDisplayString,
                    configString,
                    inputQuantityAriaLabel,
                    discountStringText,
                    originalPriceText: '',
                    currentPriceText: '',
                    shippingChargesText: shippingCharges,
                    salesAgreementPricePrompt: 'Contract price applied'
                }}
            />
        );
    };

    const _getPickUpAtStoreComponents = (pickupDeliveryLocation: string): IPickUpAtStore => {
        const pickUpAtStoreWithLocationText = 'Pick up at a store';

        return {
            PickUpAtStore: { className: 'ms-checkout__pick-up-at-store' },
            label: <span className='ms-checkout__store-label'>{pickUpAtStoreWithLocationText}</span>,
            location: <div className='ms-checkout__store-location'>{pickupDeliveryLocation}</div>
        };
    };

    const _getEmailDeliveryComponents = (): IEmailDelivery => {
        const emailDeliveryText = 'Will be delivered via email provided during checkout';
        return {
            EmailDelivery: { className: 'ms-checkout__email-delivery' },
            label: <span className='ms-checkout__email-label'>{emailDeliveryText}</span>
        };
    };

    const _getLineItemsComponents = (items: CartLine[]): ILineItem[] => {
        return items.map((cartLine, index) => {
            const pickupDeliveryLocation = _getDeliveryLocation(cartLine);
            const isPickUp = _getCartPickDeliveryMode(cartLine);
            const isEmailDelivery =
                cartLine.DeliveryMode && cartLine.DeliveryMode !== '' ? cartLine.DeliveryMode === emailDeliveryModeCode : false;
            return {
                LineId: cartLine.LineId || '',
                LineItem: { className: 'ms-checkout__line-item' },
                item: _getLineItemComponent(cartLine, index),
                pickUpAtStore: isPickUp ? _getPickUpAtStoreComponents(pickupDeliveryLocation) : undefined,
                emailDelivery: isEmailDelivery ? _getEmailDeliveryComponents() : undefined
            };
        });
    };

    const _getCartPickDeliveryMode = (cartLineItem: CartLine): boolean => {
        return !StringExtensions.isNullOrWhitespace(cartLineItem.DeliveryMode)
            ? retailMultiplePickUpOptionEnabled
                ? cartLineItem.DeliveryMode ===
                  channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLineItem.DeliveryMode)
                : cartLineItem.DeliveryMode === pickupDeliveryModeCode
            : false;
    };

    const _getCartShipDeliveryMode = (cartLineItem: CartLine): boolean => {
        return !StringExtensions.isNullOrWhitespace(cartLineItem.DeliveryMode)
            ? retailMultiplePickUpOptionEnabled
                ? cartLineItem.DeliveryMode !==
                  channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLineItem.DeliveryMode)
                : cartLineItem.DeliveryMode !== pickupDeliveryModeCode
            : false;
    };

    const _getGroupTitleComponent = (count: number, type: deliveryType): string => {
        let emailDeliveryModeDesc: string = '';

        deliveryOptions?.find(productDeliveryOptions => {
            emailDeliveryModeDesc = emailDeliveryModeDesc
                ? emailDeliveryModeDesc
                : productDeliveryOptions.DeliveryOptions?.find(deliveryOption => deliveryOption.Code === emailDeliveryModeCode)
                      ?.Description!;
        });

        const suffix = count > 1 ? itemsLabel : itemLabel;
        const title =
            type === 'ship'
                ? shippingCountCheckoutLineItem
                : type === 'pickup'
                ? inStorePickUpLabel
                : `${emailDeliveryModeDesc} (${count} ${suffix})`;
        return title.replace('{count}', count.toString()).replace('{suffix}', suffix);
    };

    const _countItems = (items: CartLine[]): number => {
        return items.reduce((count, item) => {
            return count + (item.Quantity || 0);
        }, 0);
    };

    const _filterItemsByDiliveryType = (type: deliveryType): CartLine[] => {
        const cart = cartState.cart;
        if (type === 'ship') {
            return (
                (cart.CartLines &&
                    cart.CartLines.filter((cartLine: CartLine) =>
                        cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                            ? _getCartShipDeliveryMode(cartLine) && cartLine.DeliveryMode !== emailDeliveryModeCode
                            : cartLine
                    )) ||
                []
            );
        } else if (type === 'pickup') {
            return (
                cart.CartLines?.filter((cartLine: CartLine) =>
                    cartLine.DeliveryMode && cartLine.DeliveryMode !== '' ? _getCartPickDeliveryMode(cartLine) : null
                ) || []
            );
        }
        return cart.CartLines?.filter((cartLine: CartLine) => cartLine.DeliveryMode === emailDeliveryModeCode) || [];
    };

    const _getLineItemsByDeliveryType = (type: deliveryType): ILineItemDeliveryGroup | undefined => {
        if ((pickupDeliveryModeCode === '' || pickupDeliveryModeCode === undefined) && (type === 'pickup' || type === 'email')) {
            return;
        }

        const items = _filterItemsByDiliveryType(type);

        if (items.length === 0) {
            return undefined;
        }

        const count = _countItems(items);
        const title = _getGroupTitleComponent(count, type);

        return {
            LineItemDeliveryGroup: { className: classnames('ms-checkout__line-items-delivery-group', type) },
            LineItemList: { className: 'ms-checkout__line-items-list' },
            heading: <div className='ms-checkout__line-items-group-title'>{title}</div>,
            lineItems: _getLineItemsComponents(items)
        };
    };

    const _getLineItems = (): ILineItems | undefined => {
        const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.EditCart);
        const attribute = getTelemetryAttributes(telemetryContent!, payLoad);
        const cart = cartState.cart;
        const groupClass = retailMultiplePickUpOptionEnabled ? ' multiple-pickup-enabled' : '';
        if (!cart || !cart.CartLines || cart.CartLines.length === 0 || !products) {
            return undefined;
        }

        return {
            LineItems: { className: `ms-checkout__line-items${groupClass}` },
            Header: { className: 'ms-checkout__line-items-header' },
            heading: undefined,
            editLink: (
                <Button
                    className='ms-checkout__line-items-edit-cart-link'
                    title={editCartText}
                    color='link'
                    href={Msdyn365.getUrlSync('cart', context.actionContext) || ''}
                    {...attribute}
                >
                    {editCartText}
                </Button>
            ),
            itemsForPickup: _getLineItemsByDeliveryType('pickup'),
            itemsForShip: _getLineItemsByDeliveryType('ship'),
            itemsForEmail: _getLineItemsByDeliveryType('email'),
            itemsGroupWithMulitplePickupMode: retailMultiplePickUpOptionEnabled
                ? _getLineItemsByDeliveryTypeWithMulitplePickupMode()
                : undefined
        };
    };

    const _getGroupTitleComponentWithMultiplePickUp = (
        count: number,
        type: deliveryType,
        deliveryMode?: string,
        fulFillmentStoreId?: string
    ): string => {
        let pickupDeliveryModeDesc: string = '';
        let emailDeliveryModeDesc: string = '';

        deliveryOptions?.find(productDeliveryOptions => {
            emailDeliveryModeDesc = emailDeliveryModeDesc
                ? emailDeliveryModeDesc
                : productDeliveryOptions.DeliveryOptions?.find(deliveryOption => deliveryOption.Code === emailDeliveryModeCode)
                      ?.Description!;
        });

        deliveryOptions?.find(productDeliveryOptions => {
            pickupDeliveryModeDesc = pickupDeliveryModeDesc
                ? pickupDeliveryModeDesc
                : productDeliveryOptions.DeliveryOptions?.find(deliveryOption => deliveryOption.Code === deliveryMode)?.Description!;
        });

        const pickupTitle: string = `${pickupDeliveryModeDesc}, ${fulFillmentStoreId}`;
        const suffix = count > 1 ? itemsLabel : itemLabel;
        const title =
            type === 'ship'
                ? `(${count} ${suffix})`
                : type === 'pickup'
                ? `${pickupTitle} (${count} ${suffix})`
                : `${emailDeliveryModeDesc} (${count} ${suffix})`;
        return title.replace('{count}', count.toString()).replace('{suffix}', suffix);
    };

    const _filterItemsByMultiplePickupMode = (): CartLine[][] => {
        const cart = cartState.cart;
        const getGroupByStorePickup = (items: CartLine[]) => groupBy(items, (item: CartLine) => item.FulfillmentStoreId);
        const getGroupByDelivery = (items: CartLine[]) => groupBy(items, (item: CartLine) => item.DeliveryMode);
        const groupDelivery: Record<string, CartLine[]> = getGroupByDelivery(cart?.CartLines || []);
        const cartLinesGroup: CartLine[] = [];
        const cartLinesGrp: CartLine[][] = [];

        // 1) Group by store and pick up mode
        Object.entries(groupDelivery).forEach(([deliveryMode, groupByDeliveryType]) => {
            // @ts-ignore
            groupDelivery[deliveryMode] = getGroupByStorePickup(groupByDeliveryType);
            cartLinesGroup.push(getGroupByStorePickup(groupByDeliveryType));
        });

        Object.keys(cartLinesGroup).forEach(key => {
            const cartLines = cartLinesGroup[key as keyof typeof cartLinesGroup];
            Object.keys(cartLines).forEach(index => {
                const cartLine = cartLines[index as keyof typeof cartLines];
                cartLinesGrp.push(cartLine);
            });
        });

        return cartLinesGrp;
    };

    const _getLineItemsByDeliveryTypeWithMulitplePickupMode = (): ILineItemDeliveryGroup[] | undefined => {
        if (pickupDeliveryModeCode === '' || pickupDeliveryModeCode === undefined) {
            return;
        }

        const items = _filterItemsByMultiplePickupMode();

        if (items.length === 0) {
            return undefined;
        }

        const lineItemDeliveryGroup: ILineItemDeliveryGroup[] = [];
        items.map(item => {
            const count = _countItems(item);
            const cartLine = item[0];
            const pickupDeliveryLocation = _getDeliveryLocation(cartLine);
            const type: deliveryType =
                cartLine.FulfillmentStoreId && cartLine.DeliveryMode
                    ? 'pickup'
                    : cartLine.FulfillmentStoreId === '' &&
                      cartLine.DeliveryMode === emailDeliveryModeCode &&
                      !StringExtensions.isNullOrEmpty(emailDeliveryModeCode)
                    ? 'email'
                    : 'ship';
            const title = _getGroupTitleComponentWithMultiplePickUp(count, type, cartLine.DeliveryMode, pickupDeliveryLocation);
            const groupTitle = type === 'pickup' ? multiplePickUpLabel : type === 'ship' ? shippingLable : 'email';
            const iconClass = `ms-checkout__line-items-group-title-multiple-pickup-${type}icon`;
            lineItemDeliveryGroup.push({
                LineItemDeliveryGroup: { className: classnames('ms-checkout__line-items-delivery-group multiple-pickup', type) },
                LineItemList: { className: 'ms-checkout__line-items-list' },
                lineItemWraperIcon: <div className={iconClass} />,
                lineItemWraper: <div className='ms-checkout__line-items-group-title-multiple-pickup-heading'>{groupTitle}</div>,
                heading: <div className='ms-checkout__line-items-group-title-multiple-pickup-subheading'>{title}</div>,
                lineItems: _getLineItemsComponents(item)
            });
        });

        return lineItemDeliveryGroup;
    };

    return _getLineItems();
};
